/**
 * Hook de permissoes por modulo.
 *
 * Regra:
 *   - owner / admin = acesso total a todos os modulos
 *   - member (e qualquer outro role) = acesso restrito a allowedModules
 *
 * Uso:
 *   const { isAdmin, hasModule } = usePermissions();
 *   if (!hasModule('financial')) return null;
 */

import { useAuthStore } from './auth-store';
import type { AllowedModule } from './api-client';

export type AppModule = AllowedModule;

export interface Permissions {
  /** true para owner ou admin — acesso irrestrito */
  isAdmin: boolean;
  /** Verifica se o usuario tem acesso ao modulo. Owner/admin sempre true. */
  hasModule(module: AppModule): boolean;
  /** Lista dos modulos permitidos (null = todos, para admin) */
  modules: AppModule[] | null;
}

const ADMIN_ROLES = new Set(['owner', 'admin']);

export function usePermissions(): Permissions {
  const legacyRole = useAuthStore((s) => s.legacyRole);
  const allowedModules = useAuthStore((s) => s.allowedModules);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  const isAdmin =
    isSuperAdmin || (legacyRole != null && ADMIN_ROLES.has(legacyRole));

  function hasModule(module: AppModule): boolean {
    if (isAdmin) return true;
    if (!allowedModules) return false;
    return allowedModules.includes(module);
  }

  return {
    isAdmin,
    hasModule,
    modules: isAdmin ? null : (allowedModules ?? []),
  };
}
